import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';

export interface EmployeeOption {
  id: string;
  name: string;
  registration: string;
  profileImagePath?: string;
  displayName?: string;
}

@Component({
  selector: 'app-employee-select',
  templateUrl: './employee-select.component.html',
  styleUrls: ['./employee-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmployeeSelectComponent),
      multi: true,
    },
  ],
})
export class EmployeeSelectComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() items: EmployeeOption[] = [];
  @Input() placeholder: string = 'Selecione um funcionário';
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() clearable: boolean = true;
  @Input() searchable: boolean = true;
  @Input() notFoundText: string = 'Nenhum funcionário encontrado';
  @Input() loadingText: string = 'Carregando...';
  @Input() typeToSearchText: string = 'Digite para buscar';
  @Input() minTermLength: number = 0;
  @Input() typeahead$?: Subject<string>;

  internalItems: EmployeeOption[] = [];

  @Output() change = new EventEmitter<EmployeeOption | null>();
  @Output() clear = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  selectedValue: string | null = null;
  isFocused: boolean = false;

  private onChange = (value: EmployeeOption | null) => {};
  private onTouched = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: EmployeeOption | string | null): void {
    if (typeof value === 'string') {
      this.selectedValue = value;
    } else if (value && typeof value === 'object') {
      this.selectedValue = value.id;
    } else {
      this.selectedValue = null;
    }
  }

  registerOnChange(fn: (value: EmployeeOption | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChange(value: EmployeeOption | string | null): void {
    if (!value) {
      this.selectedValue = null;
      this.onChange(null);
      this.onTouched();
      this.change.emit(null);
      return;
    }

    if (typeof value === 'string') {
      const item = this.internalItems.find(item => item.id === value);
      if (item) {
        this.selectedValue = item.id;
        this.onChange(item);
        this.onTouched();
        this.change.emit(item);
      } else {
        this.selectedValue = value;
        this.onChange(null);
        this.onTouched();
        this.change.emit(null);
      }
    } else {
      this.selectedValue = value.id;
      this.onChange(value);
      this.onTouched();
      this.change.emit(value);
    }
  }

  onClear(): void {
    this.onSelectionChange(null);
    this.clear.emit();
  }

  onOpen(): void {
    this.isFocused = true;
    this.internalItems = [...(this.items || [])];
  }

  onClose(): void {
    this.isFocused = false;
  }

  onFocus(): void {
    this.isFocused = true;
    this.focus.emit();
  }

  onBlur(): void {
    this.isFocused = false;
    this.onTouched();
    this.blur.emit();
  }

  compareWith = (a: any, b: any): boolean => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;

    const aId = typeof a === 'string' ? a : a?.id;
    const bId = typeof b === 'string' ? b : b?.id;

    if (aId != null && bId != null) return aId === bId;
    return a === b;
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.internalItems = [...(this.items || [])];

      if (this.selectedValue) {
        const itemExists = this.internalItems.find(item => item.id === this.selectedValue);
        if (!itemExists) {
          this.selectedValue = null;
        }
      }

      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    this.internalItems = [...(this.items || [])];
  }
}
